import * as MaterialColors from '@material-ui/core/colors';
import sortBy from 'lodash/sortBy';

// Avoids circular dependency from importing ChartUtils, which uses this module
import * as ChartTooltips from '../../../utils/ChartTooltips';
import { craverColors } from '../../../data/Constants';

const TIMESCALES = Object.freeze({
  sevenDays: '7days',
  thirtyDays: '30days',
  allTime: 'allTime',
});

// ~ 130 colors
const getDynamicMaterialColors = () => {
  let colors = ['#F56D1D', '#F8A117', '#EBC22F', '#E0592A', '#E02A85', '#F768B0', '#C47589', '#B893E6', '#97AFE2', '#58B6E7', '#7EDCDE', '#81F7D4', '#81F7D4', '#3FCC7A', '#3FCC44'];

  const materialColorEntries = Object.entries(MaterialColors);
  // Without a shuffle, the colors are in rainbow order. The shuffle adds a bit more contrast.
  const constantOrderShuffledColorEntries = sortBy(materialColorEntries, ([key, value]) => key);
  const colorWeights = [400, 700, 300, 900, 200, 50];
  colorWeights.forEach((weight) => {
    constantOrderShuffledColorEntries.forEach((colorEntry) => {
      const [colorName, weightToValueMap] = colorEntry;
      const colorValue = weightToValueMap[weight];
      if (colorValue) colors.push(colorValue);
    });
  });
  return colors;
};
const PIE_DATA_AND_LINE_DATASET_COLORS = getDynamicMaterialColors();

const DEFAULT_SINGLE_LINE_COLOR = '#E0592A';

const ORDERS_ANALYTICS_API_ROUTE = 'reports/home-analytics';
const ORDERS_ANALYTICS_ROUTE_REGEX = /reports\/home-analytics/;
const DOWNLOADS_ANALYTICS_API_ROUTE = 'reports/home-app-metrics';
const APP_DOWNLOADS_API_ROUTE_REGEX = /reports\/home-app-metrics/;

/*
A react-chart-js-2 props.options object follows that of the chart-js-2 options object.
Composing the objects below can lead to an object with up to these keys for its highest-level structure:

  options: {
    animation: {
      <animations_options>
    },
    cutoutPercentage: // Pie chart option
    elements: {
      line: {
        tension: // No bezier curves
      }
    },
    hover: {
      <hover_options>
    },
    legend: {
      <legend_options>
    },
    maintainAspectRatio: boolean,
    responsiveAnimationDuration: number,
    scales: {
      xAxes: {[<one_or_more_config_objects_for_single_or_multiple_axes>]},
      yAxes: {[<one_or_more_config_objects_for_single_or_multiple_axes>]},
    },
    tooltips: {
      callbacks: {
        key: <dynamic_tooltip_content_func>,
        key2: <dynamic_tooltip_content_func2>,
      },
      ...<other_options>,
    },
  }
*/

const BASE_TOOLTIP_OPTIONS = {
  titleFontSize: 16,
  titleFontFamily: "'Lato', sans-serif",
  titleMarginBottom: 5,
  displayColors: false,
  bodyFontSize: 10,
  xPadding: 15,
  footerMarginTop: 4,
  cornerRadius: 1,
};

const BASE_DYNAMIC_TOOLTIP_CONTENT = {
  title: ChartTooltips.getYLabelOfPoint,
  label: ChartTooltips.getDateLabelOfPoint,
};

const NO_ANIMATIONS_FOR_PERFORMANCE = {
  animation: {
    duration: 0, // general animation time
  },
  hover: {
    animationDuration: 0,
  },
  responsiveAnimationDuration: 0, // animation duration after a resize
  elements: {
    line: {
      tension: 0, // disables bezier curves
    },
  },
};

const BASE_PIE_CHART_OPTIONS = {
  cutoutPercentage: 75,
  tooltips: {
    ...BASE_TOOLTIP_OPTIONS,
    displayColors: true,
    callbacks: {
      title: ChartTooltips.getCountStatOfPieSlice,
      label: ChartTooltips.getLabelOfPieSlice,
    },
  },
  maintainAspectRatio: false,
};

const NO_ANIMATION_PIE_CHART_OPTIONS = {
  ...BASE_PIE_CHART_OPTIONS,
  animation: { animateRotate: false },
};

const BASE_Y_AXIS_CONFIG = {
  ticks: { // `ticks` are the marks on an axis representing a value, e.g. the 10, 20, 30
    beginAtZero: true,
    precision: 0, // How many digits to round to
    suggestedMin: 10,
    maxTicksLimit: 4,
  },
  type: 'linear',
};

const COMING_SOON_Y_AXIS_CONFIG = {
  ticks: { // `ticks` are the marks on an axis representing a value, e.g. the 10, 20, 30
    beginAtZero: true,
    precision: 0, // How many digits to round to
    max: 100,
    maxTicksLimit: 4,
    stepSize: 25,
  },
  type: 'linear',
};

const BASE_X_AXIS_CONFIG = {
  gridLines: {
    display: false,
  },
  type: 'category',
};
const BASE_AXES_CONFIG_OPTIONS = {
  scales: {
    yAxes: [BASE_Y_AXIS_CONFIG],
    xAxes: [BASE_X_AXIS_CONFIG],
  },
};


// Keep maintainAspectRatio: false here and in other chart options.
// That, and a position: relative parent container with percent height/width is how the chart resizes properly if the parent element changes size.
const SIGNUPS_CHART_OPTIONS = {
  tooltips: {
    ...BASE_TOOLTIP_OPTIONS,
    callbacks: BASE_DYNAMIC_TOOLTIP_CONTENT,
  },
  maintainAspectRatio: false,
  ...BASE_AXES_CONFIG_OPTIONS,
};

const SIGNUPS_CHART_NO_ANIMATION_OPTIONS = {
  ...SIGNUPS_CHART_OPTIONS,
  ...NO_ANIMATIONS_FOR_PERFORMANCE,
};

const COMING_SOON_DOWNLOADS_AXES = {
  scales: {
    yAxes: [COMING_SOON_Y_AXIS_CONFIG],
    xAxes: [BASE_X_AXIS_CONFIG],
  },
};

const DOWNLOADS_CHART_OPTIONS = {
  tooltips: {
    ...BASE_TOOLTIP_OPTIONS,
    callbacks: BASE_DYNAMIC_TOOLTIP_CONTENT,
  },
  maintainAspectRatio: false,
  ...COMING_SOON_DOWNLOADS_AXES,
};

const DOWNLOADS_CHART_NO_ANIMATION_OPTIONS = {
  ...DOWNLOADS_CHART_OPTIONS,
  ...NO_ANIMATIONS_FOR_PERFORMANCE,
};

const ANALYTICS_TIMESCALE_OPTIONS = [
  { key: TIMESCALES.sevenDays, name: 'Last 7 days' },
  { key: TIMESCALES.thirtyDays, name: 'Last 30 days' },
  { key: TIMESCALES.allTime, name: 'All Time' },
];

const CHART_TITLES_CONFIG = {
  downloads: {
    titleFirstSegment: 'Downloads',
    totalsToCalculate: [{ responseLookupKey: 'downloads_total' }],
    routeKey: DOWNLOADS_ANALYTICS_API_ROUTE,
  },
  signups: {
    titleFirstSegment: 'Sign Ups',
    totalsToCalculate: [{ responseLookupKey: 'users' }],
    routeKey: ORDERS_ANALYTICS_API_ROUTE,
  },
  orders: {
    titleFirstSegment: 'Orders',
    totalsToCalculate: [
      { responseLookupKey: 'orders' },
      { responseLookupKey: 'sales', format: 'currency' },
    ],
    routeKey: ORDERS_ANALYTICS_API_ROUTE,
  },
};

const CHART_POINT_OPTIONS = {
  lineCharts: {
    downloads_app_store: {
      pointWidth: 0,
      lineColor: craverColors[0],
      pointColor: craverColors[0],
    },
    downloads_google_play: {
      pointWidth: 0,
      lineColor: craverColors[2],
      pointColor: craverColors[2],
    },
    downloads_total: {
      pointWidth: 0,
      lineColor: '#000000',
      pointColor: '#000000',
    },
    orders: {
      pointWidth: 0,
      lineColor: craverColors[0],
      pointColor: craverColors[0],
    },
    signups: {
      pointWidth: 5,
      lineColor: craverColors[0],
      pointColor: craverColors[0],
    },
  },
};

const STATIC_LEGENDS = {
  downloads: [
    { title: 'iOS', color: craverColors[0] },
    { title: 'Android', color: craverColors[2] },
    { title: 'Total', color: '#000000' },
  ],
};

export {
  TIMESCALES,
  ANALYTICS_TIMESCALE_OPTIONS,
  BASE_TOOLTIP_OPTIONS,
  CHART_POINT_OPTIONS,
  CHART_TITLES_CONFIG,
  DOWNLOADS_CHART_OPTIONS,
  SIGNUPS_CHART_OPTIONS,
  STATIC_LEGENDS,
  ORDERS_ANALYTICS_API_ROUTE,
  DOWNLOADS_ANALYTICS_API_ROUTE,
  ORDERS_ANALYTICS_ROUTE_REGEX,
  APP_DOWNLOADS_API_ROUTE_REGEX,
  DOWNLOADS_CHART_NO_ANIMATION_OPTIONS,
  NO_ANIMATIONS_FOR_PERFORMANCE,
  SIGNUPS_CHART_NO_ANIMATION_OPTIONS,
  BASE_PIE_CHART_OPTIONS,
  NO_ANIMATION_PIE_CHART_OPTIONS,
  PIE_DATA_AND_LINE_DATASET_COLORS,
  DEFAULT_SINGLE_LINE_COLOR,
  BASE_AXES_CONFIG_OPTIONS,
};
